<template>
  <div>
    <v-container class="customer-search">
      <v-row>
        <v-col sm="12">
          <h2>DAC6 Registration</h2>
          <p>
            Enter the client number and click on 'Retrieve details'.
          </p>
        </v-col>
        <v-col sm="4">
          <v-text-field
            v-model="relation_number"
            :disabled="loading_customer"
            label="Client number*"
            hide-details="auto"
            clearable
            dense
            outlined
            @keypress="pressEnter($event)"
          ></v-text-field>
        </v-col>
        <v-col sm="2">
          <v-btn
            class="primary-btn input-group-btn"
            :disabled="!relation_number"
            :loading="loading_customer"
            v-on:click="searchCustomer()"
          >
            Retrieve details
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-if="no_results">
        <v-col>
          <p color="bdoRed">No results</p>
        </v-col>
      </v-row>
    </v-container>
    <v-container class="mt-10" v-if="customer.name && !loading_customer">
      <v-row>
        <v-col class="customer-label customer-border-bottom" sm="3">
          <p>Client</p>
        </v-col>
        <v-col class="customer-value customer-border-bottom" sm="3">
          <p>{{ customer.name }}</p>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="customer-label customer-border-bottom" sm="3">
          <p>Client number</p>
        </v-col>
        <v-col class="customer-value customer-border-bottom" sm="3">
          <p>{{ customer.relation_number }}</p>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="customer-label customer-border-bottom" sm="3">
          <p>Member state</p>
        </v-col>
        <v-col class="customer-value customer-border-bottom" sm="3">
          <p>{{ customer.region }}</p>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="customer-label customer-border-bottom" sm="3">
          <p>Registration date</p>
        </v-col>
        <v-col class="customer-value customer-border-bottom" sm="3">
          <p>{{ registrationDate }}</p>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="customer-label customer-border-bottom" sm="3">
          <p>Responsible Partner</p>
        </v-col>
        <v-col class="customer-value customer-border-bottom" sm="3">
          <p>{{ customer.responsible_partner }}</p>
        </v-col>
      </v-row>
      <v-row>
        <v-col sm="6">
          <p>Enter the job number, if any, and a description of the arrangement:</p>
        </v-col>
      </v-row>
      <v-form v-model="valid">
        <v-row>
          <v-col sm="6">
            <ul class="wrapper customer-label">
              <li class="wrapper-row">
                <label for="job_number">Job number</label>
                <v-text-field id="job_number" v-model="job_number" outlined dense></v-text-field>
              </li>
              <li class="wrapper-row">
                <label for="implementation_date"
                  >Date (start 30-day period)*
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon dense v-bind="attrs" v-on="on">
                        mdi-help-circle
                      </v-icon>
                    </template>
                    <span
                      >This is the date on which the arrangement has been made ready for implementation / has been
                      implemented / the first step of implementation has been made. Whichever occurs first.
                    </span>
                  </v-tooltip>
                </label>
                <v-menu
                  v-model="implementation_date_menu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  max-width="276"
                  transition="scale-transition"
                  offset-y
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="computedDateFormatted"
                      label=""
                      prepend-inner-icon="mdi-calendar-range"
                      class="meta-left dateField"
                      outlined
                      dense
                      v-on="on"
                      readonly
                      :rules="required"
                    />
                  </template>
                  <v-date-picker
                    v-model="implementation_date"
                    :value="computedDateFormatted"
                    :first-day-of-week="1"
                    @input="implementation_date_menu = false"
                  />
                </v-menu>
              </li>
              <li class="wrapper-row">
                <label for="task_remark">Description of arrangement*</label>
                <v-textarea
                  v-model="task_remark"
                  id="task_remark"
                  value=""
                  rows="3"
                  outlined
                  :rules="rulesTaskRemark"
                ></v-textarea>
              </li>
            </ul>
          </v-col>
          <v-col sm="12">
            <p>
              Once you have entered the details, click on 'Generate questionnaire'. You will automatically go to the
              overview where you can click on your questionnaire to start.
            </p>
            <v-btn class="primary-btn mt-2" @click="submit" :loading="loading_customer" :disabled="!valid">
              Generate questionnaire
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-container>
  </div>
</template>

<script>
import helpers from '@/plugins/helpers'
import config from '@/config'
import { format } from 'date-fns'
export default {
  data: () => ({
    customer: {
      id: null,
      name: '',
      region: '',
      responsible_partner: ''
    },
    loading_customer: false,
    no_results: false,
    relation_number: '',
    job_number: '',
    implementation_date_menu: false,
    implementation_date: '',
    task_remark: '',
    valid: false,
    required: [v => !!v || 'This field is required'],
    rulesTaskRemark: [
      v => !!v || 'This field is required',
      v => (v || '').length < 500 || 'Description of arrangement cannot contain more than 500 characters'
    ]
  }),
  computed: {
    computedDateFormatted() {
      return this.implementation_date ? format(this.implementation_date, 'DD-MM-YYYY') : ''
    },
    registrationDate() {
      return format(new Date(), 'DD-MM-YYYY')
    }
  },
  watch: {},
  methods: {
    async searchCustomer() {
      this.loading_customer = true
      let response = await helpers.fetchApi(config.apiUrl + 'customer/' + this.relation_number, 'GET')
      if (response) {
        if (response.customer) {
          this.no_results = false
          this.customer = response.customer
        } else {
          this.customer = {
            id: null,
            name: ''
          }
          this.no_results = true
        }
        this.loading_customer = false
      }
    },

    async submit() {
      this.$store.commit('general/SET_LOADING', { state: true })
      let response = await helpers.fetchApi(config.apiUrl + 'customer-questionnaire', 'POST', {
        customer_questionnaire: {
          customer_id: this.customer.id,
          relation_number: this.customer.relation_number,
          name: this.customer.name,
          region: this.customer.region,
          responsible_partner: this.customer.responsible_partner,
          task_number: this.job_number,
          task_remark: this.task_remark,
          implementation_date: this.implementation_date
        }
      })
      if (response) {
        if (response.success) {
          this.$router.push({ path: `/${this.$route.params.countrycode}/questionnaires` })
        } else {
          console.log('Something went wrong')
        }
        this.$store.commit('general/SET_LOADING', { state: false })
      }
    },
    pressEnter(event) {
      if (event.charCode === 13) this.searchCustomer()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/style/_variables.scss';
.input-group-btn {
  height: 40px !important;
}

.customer-label p,
.customer-value p,
.customer-label span {
  width: 100%;
  font-size: 12pt;
}
.customer-label p,
.customer-label span,
.wrapper label {
  font-weight: bold;
  font-size: 12pt;
}

.wrapper {
  list-style-type: none;
  padding: 0px;
}
.wrapper-row {
  display: flex;
  justify-content: flex-end;
}
.wrapper-row > label {
  padding: 0.5em 1em 0.5em 0;
  flex: 2;
}
.wrapper-row > .v-text-field,
.wrapper-row > .v-textarea {
  flex: 2;
}
.customer-border-bottom {
  border-bottom: 1px solid #ddd;

  & p {
    margin-bottom: 0;
  }
}
</style>
